import { memo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { CommandPaletteAction } from "components/CommandPalette/types";
import BaseActionButton from "ds/components/BaseAction/Button";
import Typography from "ds/components/Typography";
import Icon from "ds/components/Icon";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useCommandPaletteExecuteAction from "components/CommandPalette/hooks/useExecuteAction";
import useCommandPaletteStore from "components/CommandPalette/store/useCommandPaletteStore";
import TextEllipsis from "ds/components/TextEllipsis";
import KeyboardHint from "ds/components/KeyboardHint";
import { Cross } from "components/icons/generated";
import useAnalytics from "hooks/useAnalytics";

import styles from "./styles.module.css";

type CommandPaletteActionItemProps = {
  action: CommandPaletteAction;
  isActive: boolean;
  onRemove?: () => void;
};

const CommandPaletteActionItem = ({
  action,
  isActive,
  onRemove,
}: CommandPaletteActionItemProps) => {
  const trackAnalyticsEvent = useAnalytics();

  const executeAction = useCommandPaletteExecuteAction();
  const { isKeyboardNavigating, setActiveActionId, setIsKeyboardNavigating } =
    useCommandPaletteStore(
      useShallow((state) => ({
        isKeyboardNavigating: state.isKeyboardNavigating,
        setActiveActionId: state.setActiveActionId,
        setIsKeyboardNavigating: state.setIsKeyboardNavigating,
      }))
    );

  // Resolve conflict between keyboard and mouse navigation
  const handleMouseMove = useCallback(() => {
    if (isKeyboardNavigating) {
      setIsKeyboardNavigating(false);
    }
  }, [isKeyboardNavigating, setIsKeyboardNavigating]);

  const handleMouseEnter = useCallback(() => {
    if (!isKeyboardNavigating) {
      setActiveActionId(action.id, "mouse");
    }
  }, [isKeyboardNavigating, setActiveActionId, action.id]);

  const handleFocus = useCallback(() => {
    // Only set active on focus if not already active (prevents mouse click from triggering scroll)
    if (!isActive) {
      setActiveActionId(action.id, "keyboard");
    }
  }, [setActiveActionId, action.id, isActive]);

  const handlePress = useCallback(() => {
    executeAction(action);

    trackAnalyticsEvent("Command palette: mouse action executed", {
      actionId: action.id,
      actionType: action.type,
      actionTitle: action.title,
    });
  }, [executeAction, action, trackAnalyticsEvent]);

  return (
    <Box
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      className={styles.actionItemWrapper}
      data-active={isActive}
      relative
    >
      <BaseActionButton
        className={styles.actionItem}
        data-active={isActive}
        onPress={handlePress}
        fullWidth
      >
        {action.icon && <Icon src={action.icon} size="default" noShrink />}
        <span className={styles.titleWrapper}>
          <TextEllipsis tooltip={action.title} tooltipWidthMode="maxWidthSm">
            {(props) => (
              <Typography {...props} variant="p-body3" tag="span">
                {action.title}
              </Typography>
            )}
          </TextEllipsis>
          {action.metadata?.title && (
            <TextEllipsis tooltip={action.metadata.title} tooltipWidthMode="maxWidthSm">
              {(props) => (
                <Typography
                  {...props}
                  variant="p-body3"
                  tag="span"
                  color="secondary"
                  className={styles.metadata}
                >
                  &nbsp;-&nbsp;{action.metadata!.title}
                </Typography>
              )}
            </TextEllipsis>
          )}
        </span>
        <KeyboardHint keys="↵" label="Select" className={styles.keyboardHint} />
      </BaseActionButton>
      {onRemove && (
        <ButtonIcon
          size="small"
          variant="ghost"
          icon={Cross}
          onPress={onRemove}
          className={styles.removeButton}
        >
          Remove from recent
        </ButtonIcon>
      )}
    </Box>
  );
};

export default memo(CommandPaletteActionItem);
